/**** apsDataCards.jsx ****/
import React, { useMemo } from "react";
import { styled } from "@mui/system";
import CircularProgress from "@mui/material/CircularProgress";
import { displayValue, formatNumber, roundToDecimals } from "../../../utils/generalUtils";
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
    GraphicComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
// Estilos
import {
    CategoryContainer,
    VariationLabel,
    CategoryTitle,
    ChartContainer,
    AreaTotalContainer,
    ChartsRow,
    VariationContainer,
    VariationDosisContainer,
    VariationValue,
} from "./apsDataCardsStyle";

// Register ECharts modules
echarts.use([LineChart, BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, GraphicComponent, CanvasRenderer]);

// Colores de marca desde variables CSS globales
const PRIMARY_COLOR = "var(--primary-color)";
const PRIMARY_COLOR_DARK = "var(--primary-color-dark)";
const PRIMARY_COLOR_HEX = "#2563EB";
const PRIMARY_COLOR_DARK_HEX = "#1D4ED8";

const glassTooltip = {
    backgroundColor: 'rgba(255,255,255,0.88)',
    borderColor: 'rgba(0,0,0,0.08)',
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: '#0F172A', fontSize: 13 },
    extraCssText: 'backdrop-filter:blur(8px);border-radius:8px;box-shadow:0 4px 6px -1px rgba(0,0,0,0.07);',
};

/* ───────────────── helpers ───────────────── */
const VarDashboardCategoryTitle = styled("h3")({
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "40px",
    margin: "0 0 20px",
    padding: "10px 16px",
    borderRadius: "6px",
    color: "#fff",
    background: "linear-gradient(135deg, #2d3748 0%, #3d4a5c 100%)",
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "0.3px",
    textAlign: "center",
    textTransform: "uppercase",
    boxSizing: "border-box",
    "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "4px",
        borderRadius: "2px",
        background: "var(--primary-color)",
    },
});

function renderCategory(title, content, useVarDashboardTitle = false) {
    return (
        <CategoryContainer>
            {useVarDashboardTitle ? (
                <VarDashboardCategoryTitle>{title}</VarDashboardCategoryTitle>
            ) : (
                <CategoryTitle>{title}</CategoryTitle>
            )}
            {content}
        </CategoryContainer>
    );
}

/** Quita NaN/undefined y ceros explícitos */
const cleanNumbers = (arr) =>
    (arr || []).filter((n) => n !== null && n !== undefined && !isNaN(n));


/** Placeholder centrado y elegante para “No hay datos” */
const EmptyState = styled("div")(({ theme }) => ({
    minHeight: 340,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontWeight: 700,
    fontSize: "1.35rem",
    letterSpacing: 0.2,
    color:
        (theme && theme.palette && theme.palette.grey && theme.palette.grey[400]) ||
        "#BDBDBD",
    userSelect: "none",
}));

const VarMetricsGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: "14px",
    padding: "12px 4px 4px",
    "@media (max-width: 1360px)": {
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
    "@media (max-width: 900px)": {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 560px)": {
        gridTemplateColumns: "1fr",
    },
});

// Tarjetas exclusivas de VAR inspiradas en el estilo minimalista del Dashboard:
// borde lateral naranja, jerarquía tipográfica clara y valores grandes.
const VarMetricCard = styled("div")({
    minHeight: "116px",
    borderRadius: "12px",
    padding: "18px 18px 16px 16px",
    backgroundColor: "#fff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderLeft: "4px solid var(--primary-color)",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    textAlign: "left",
    transition: "box-shadow 160ms ease, transform 160ms ease",
    "&:hover": {
        boxShadow: "0 5px 14px rgba(15, 23, 42, 0.10)",
        transform: "translateY(-1px)",
    },
});

const VarMetricLabel = styled("div")({
    color: "#64748B",
    fontSize: "0.94rem",
    fontWeight: 650,
    lineHeight: 1.25,
});

const VarMetricValue = styled("div")({
    color: "#172033",
    fontSize: "2.35rem",
    fontWeight: 800,
    marginTop: "9px",
    lineHeight: 1,
    letterSpacing: "-0.04em",
});

const hasMetricValue = (value) =>
    value !== undefined && value !== null && value !== "" &&
    !(typeof value === "number" && Number.isNaN(value));

/**
 * Construye bins homogéneos para un histograma.
 * Recibe valores ya limpiados (sin ceros si aplica).
 * Devuelve [{ rango, vuelos, color }]
 */
const buildHistogram = (values, numBins = 3) => {
    const vals = cleanNumbers(values);
    if (!vals.length) return [];

    const min = Math.min(...vals);
    const max = Math.max(...vals);

    // Si todos los valores son iguales devolvemos un solo bin
    if (min === max) {
        return [
            {
                rango: `[${min.toFixed(2)}, ${max.toFixed(2)}]`,
                vuelos: vals.length,
                color: PRIMARY_COLOR, // usa color de marca
            },
        ];
    }

    const width = (max - min) / numBins;

    const bins = Array.from({ length: numBins }, (_, i) => ({
        rangoIni: min + i * width,
        rangoFin: i === numBins - 1 ? max : min + (i + 1) * width,
        conteo: 0,
    }));

    vals.forEach((v) => {
        let idx = Math.floor((v - min) / width);
        if (idx >= numBins) idx = numBins - 1;
        bins[idx].conteo += 1;
    });

    // Paleta basada en las variables de marca
    const palette = [PRIMARY_COLOR_DARK, PRIMARY_COLOR, PRIMARY_COLOR, PRIMARY_COLOR];

    return bins.map((b, i) => ({
        rango: `[${b.rangoIni.toFixed(2)}, ${b.rangoFin.toFixed(2)}]`,
        vuelos: b.conteo,
        color: palette[i % palette.length],
    }));
};

/** Dominio [min-2, max+2] */
const getAxisDomain = (arr) => {
    const clean = cleanNumbers(arr);
    if (!clean.length) return [0, 1];
    const min = Math.min(...clean);
    const max = Math.max(...clean);
    return [min - 2, max + 2];
};

/* ───────────────── componente principal ───────────────── */
export const renderApsDataCards = (
    validations,
    datosAnalisis,
    indicadores,
    onAreasCalculatedLoader,
    apsChartsRef
) => {

    /* ── Datos base ── */
    const toNumberArray = (metric) => {
        const raw = Array.isArray(metric?.raw)
            ? metric.raw
            : (Array.isArray(metric?.valor) ? metric.valor : String(metric?.valor || "").split(","));
        return raw.map((value) => parseFloat(String(value).trim())).filter((value) => !isNaN(value));
    };

    const alturasRaw = toNumberArray(datosAnalisis.alturas);

    // Para la línea: convertir 0 → null (no dibuja)
    const alturasForLine = alturasRaw.map((v) => (v === 0 ? null : v));
    // Para histogramas y dominios: eliminar ceros
    const alturasNoZero = alturasRaw.filter((v) => v !== 0);

    const dosisRealesArray = toNumberArray(datosAnalisis.dosisReales);
    const dosisAreasArray = toNumberArray(datosAnalisis.dosisAreas);

    const numeroKmls = parseInt(datosAnalisis.numeroKmls?.valor, 10) || 0;

    // Longitud máxima para ejes/series
    const maxLen = Math.max(
        numeroKmls,
        alturasRaw.length,
        dosisRealesArray.length,
        dosisAreasArray.length
    );
    const kmlIndices = Array.from({ length: maxLen }, (_, i) => i + 1);

    /* ── dominios Y para las gráficas de línea (mín-2, máx+2) ── */
    const [yMinLitros, yMaxLitros] = getAxisDomain(dosisRealesArray);
    const [yMinDosisAreas, yMaxDosisAreas] = getAxisDomain(dosisAreasArray);
    const [yMinAlturas, yMaxAlturas] = getAxisDomain(alturasNoZero);

    const unitCandidates = [
        datosAnalisis.unidadDosisReal?.valor,
        datosAnalisis.unidadDosisTeorica?.valor,
        datosAnalisis.unidadDiferenciaDosis?.valor,
        datosAnalisis.unidadVolumenMezcla?.valor,
        datosAnalisis.dosisProducto?.valor,
        datosAnalisis.dosisReal?.valor,
        datosAnalisis.dosisTeorica?.valor,
        datosAnalisis.volumenPromedio?.valor,
    ]
        .filter(Boolean)
        .join(' ');

    const isSolidApplication = /\bkg\b|kg\/ha|kilos?/i.test(unitCandidates);
    const alturaUnitLabel = String(datosAnalisis.unidadAltura?.valor || 'm').trim() || 'm';
    const amountUnitLabel = isSolidApplication ? 'Kilos' : 'Litros';
    const rateUnitLabel = isSolidApplication ? 'Kilos/Ha' : 'Litros/Ha';
    const amountChartTitle = isSolidApplication ? 'KILOS POR VUELO' : 'LITROS POR VUELO';
    const rateChartTitle = isSolidApplication ? 'KILOS POR HECTÁREA POR VUELO' : 'LITROS POR HECTÁREA POR VUELO';

    /* ───────────────── Indicadores de Área (pie + barras) ───────────────── */
    // En VAR la capa objetivo representa maleza. Las etiquetas específicas se
    // aplican solo a ese subanálisis para no alterar aplicaciones convencionales.
    const isTasaVariable = String(datosAnalisis.tipoSubAnalisis?.valor || '')
        .trim()
        .toUpperCase() === 'TASA_VARIABLE';

    const areaLabels = isTasaVariable
        ? {
            total: 'Área total de maleza',
            applied: 'aplicada en maleza',
            outside: 'aplicada fuera de maleza',
            missing: 'maleza no aplicada',
            variation: 'Maleza no aplicada',
            barTitle: 'Distribución de aplicación localizada',
        }
        : {
            total: 'Área Total del bloque',
            applied: 'aplicada',
            outside: 'sobre aplicada',
            missing: 'no aplicada',
            variation: 'Variación Áreas',
            barTitle: 'Área Aplicada No Productiva',
        };

    const totalArea =
        (indicadores.areaAplicada || 0) +
        (indicadores.areaNoAplicada || 0) +
        (indicadores.areaSobreAplicada || 0);

    const pieData = [
        {
            id: areaLabels.applied,
            label: areaLabels.applied,
            value: indicadores.areaAplicada || 0,
            color: '#4CAF50',
            percentage:
                totalArea > 0 ? ((indicadores.areaAplicada || 0) / totalArea) * 100 : 0,
        },
        {
            id: areaLabels.outside,
            label: areaLabels.outside,
            value: indicadores.areaSobreAplicada || 0,
            color: '#F44336',
            percentage:
                totalArea > 0
                    ? ((indicadores.areaSobreAplicada || 0) / totalArea) * 100
                    : 0,
        },
        {
            id: areaLabels.missing,
            label: areaLabels.missing,
            value: indicadores.areaNoAplicada || 0,
            color: '#FFEB3B',
            percentage:
                totalArea > 0 ? ((indicadores.areaNoAplicada || 0) / totalArea) * 100 : 0,
        },
    ];

    const barData = isTasaVariable
        ? [
            {
                categoria: 'Área aplicada en maleza',
                valor: indicadores.areaAplicada || 0,
                color: PRIMARY_COLOR,
            },
            {
                categoria: 'Área aplicada fuera de maleza',
                valor: indicadores.areaSobreAplicada || 0,
                color: '#F44336',
            },
        ]
        : [
            {
                categoria: 'Área aplicada en calles',
                valor: indicadores.areaSobreCalles || 0,
                color: PRIMARY_COLOR, // usa color de marca en vez de azul
            },
            {
                categoria: 'Área aplicada fuera del lote',
                valor: indicadores.areaFueraDeLote || 0,
                color: '#F44336',
            },
        ];

    const varMetricCard = (label, value, suffix = "") => (
        <VarMetricCard>
            {onAreasCalculatedLoader === false ? (
                <CircularProgress size={30} />
            ) : hasMetricValue(value) ? (
                <>
                    <VarMetricLabel>{label}</VarMetricLabel>
                    <VarMetricValue>{displayValue(value)}{suffix}</VarMetricValue>
                </>
            ) : (
                <>
                    <VarMetricLabel>{label}</VarMetricLabel>
                    <VarMetricValue>-</VarMetricValue>
                </>
            )}
        </VarMetricCard>
    );

    // VAR no requiere gráficas de distribución de áreas. El cliente solicitó
    // únicamente indicadores grandes; se mantienen intactas las
    // gráficas por vuelo de la sección inferior.
    // El área total con maleza ya viene calculada por el flujo VAR como
    // `areaMalezaHa`. Para análisis históricos se conserva un respaldo
    // matemático equivalente: aplicada en maleza + no aplicada en maleza.
    const areaConMalezaVar = hasMetricValue(indicadores.areaMalezaHa)
        ? indicadores.areaMalezaHa
        : (Number(indicadores.areaAplicada || 0) + Number(indicadores.areaNoAplicada || 0));

    // Solicitud del cliente para VAR:
    // Variación de aplicación (%) = área aplicada en maleza / área con maleza × 100.
    // Se calcula en el frontend a partir de los indicadores ya disponibles para
    // mantener el ajuste aislado al modo VAR y conservar intactos los demás análisis.
    const areaConMalezaVarNumber = Number(String(areaConMalezaVar ?? '').replace(',', '.'));
    const areaAplicadaEnMalezaVarNumber = Number(String(indicadores.areaAplicada ?? '').replace(',', '.'));
    const variacionAplicacionVar = Number.isFinite(areaConMalezaVarNumber) &&
        Number.isFinite(areaAplicadaEnMalezaVarNumber) &&
        areaConMalezaVarNumber > 0
        ? roundToDecimals((areaAplicadaEnMalezaVarNumber / areaConMalezaVarNumber) * 100, 2)
        : null;

    const varAreaIndicators = (
        <VarMetricsGrid>
            {varMetricCard('Área del lote', indicadores.areaTotalLote, ' ha')}
            {varMetricCard('Área aplicada sin maleza', indicadores.areaSobreAplicada, ' ha')}
            {varMetricCard('Área con maleza', areaConMalezaVar, ' ha')}
            {varMetricCard('Área aplicada en maleza', indicadores.areaAplicada, ' ha')}
            {varMetricCard('Área no aplicada en maleza', indicadores.areaNoAplicada, ' ha')}
            {varMetricCard('% de precisión de aplicación', variacionAplicacionVar, ' %')}
        </VarMetricsGrid>
    );

    /** Contenido de la sección de Indicadores de Área */
    const charts = (
        <div style={{ position: "relative" }}>
            {/* Área total lote */}
            <AreaTotalContainer>
                {onAreasCalculatedLoader === false ? (
                    <CircularProgress size={24} />
                ) : indicadores.areaTotalLote ? (
                    <>
                        <VariationLabel>{areaLabels.total}</VariationLabel>
                        <VariationValue>
                            {displayValue(indicadores.areaTotalLote)} H
                        </VariationValue>
                    </>
                ) : (
                    "No hay datos"
                )}
            </AreaTotalContainer>

            {/* Variación dosis */}
            <VariationDosisContainer>
                {onAreasCalculatedLoader === false ? (
                    <CircularProgress size={24} />
                ) : datosAnalisis.porcentajeVariacion?.valor ||
                indicadores.porcentajeVariacionDosis ? (
                    <>
                        <VariationLabel>Variación Dosis</VariationLabel>
                        <VariationValue>
                            {displayValue(
                                datosAnalisis.porcentajeVariacion?.valor ||
                                indicadores.porcentajeVariacionDosis
                            )}
                        </VariationValue>
                    </>
                ) : (
                    "No hay datos"
                )}
            </VariationDosisContainer>

            {/* Variación áreas */}
            <VariationContainer>
                {onAreasCalculatedLoader === false ? (
                    <CircularProgress size={24} />
                ) : indicadores.porcentajeVariacion ? (
                    <>
                        <VariationLabel>{areaLabels.variation}</VariationLabel>
                        <VariationValue>
                            {displayValue(indicadores.porcentajeVariacion)}
                        </VariationValue>
                    </>
                ) : (
                    "No hay datos"
                )}
            </VariationContainer>

            <ChartsRow>
                {/* Pie — ECharts */}
                <ChartContainer className="apsPieChart">
                    <h4
                        style={{
                            textAlign: "center",
                            marginBottom: 10,
                            fontSize: "1.5em",
                            fontWeight: "bold",
                        }}
                    >
                        Distribución de Áreas
                    </h4>
                    <ReactEChartsCore
                        echarts={echarts}
                        option={{
                            tooltip: {
                                trigger: 'item',
                                formatter: (p) => `<strong>${p.name}</strong><br/>${formatNumber(p.value, 2)} ha (${formatNumber(p.percent, 2)}%)`,
                                ...glassTooltip,
                            },
                            legend: {
                                top: 10,
                                type: 'scroll',
                                textStyle: { fontSize: 13, fontWeight: 'bold' },
                            },
                            series: [{
                                type: 'pie',
                                radius: ['40%', '70%'],
                                center: ['50%', '55%'],
                                padAngle: 1,
                                itemStyle: { borderRadius: 3, borderColor: '#fff', borderWidth: 1 },
                                data: pieData.map(d => ({
                                    name: d.label,
                                    value: roundToDecimals(d.value, 2),
                                    itemStyle: { color: d.color },
                                })),
                                label: {
                                    show: true,
                                    formatter: (p) => `${formatNumber(p.value, 2)} ha (${formatNumber(p.percent, 2)}%)`,
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    color: '#333',
                                },
                                emphasis: {
                                    label: { show: true, fontSize: 14, fontWeight: 600 },
                                    scaleSize: 6,
                                },
                            }],
                        }}
                        theme="geomotica"
                        style={{ height: 340, width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                        notMerge
                        lazyUpdate
                    />
                </ChartContainer>

                {/* Bar — Área Aplicada No Productiva — ECharts */}
                <ChartContainer className="apsBarChart">
                    <h4
                        style={{
                            textAlign: "center",
                            marginBottom: 10,
                            fontSize: "1.5em",
                            fontWeight: "bold",
                        }}
                    >
                        {areaLabels.barTitle}
                    </h4>
                    <ReactEChartsCore
                        echarts={echarts}
                        option={{
                            tooltip: {
                                trigger: 'axis',
                                axisPointer: { type: 'shadow' },
                                formatter: (params) => {
                                    const p = params[0];
                                    return `<strong>${p.name}</strong><br/>${formatNumber(p.value, 2)} ha`;
                                },
                                ...glassTooltip,
                            },
                            xAxis: {
                                type: 'value',
                                axisLabel: { formatter: (v) => formatNumber(v, 2), fontSize: 14, fontWeight: 'bold' },
                                name: 'Hectáreas',
                                nameLocation: 'middle',
                                nameGap: 42,
                                nameTextStyle: { fontSize: 16, fontWeight: 'bold' },
                                splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
                            },
                            yAxis: {
                                type: 'category',
                                data: barData.map(d => d.categoria),
                                axisLabel: { show: false },
                            },
                            legend: {
                                top: 10,
                                data: barData.map(d => d.categoria),
                                textStyle: { fontSize: 13, fontWeight: 'bold' },
                            },
                            series: [{
                                type: 'bar',
                                data: barData.map(d => ({
                                    value: roundToDecimals(d.valor, 2),
                                    name: d.categoria,
                                    itemStyle: { color: d.color === PRIMARY_COLOR ? PRIMARY_COLOR_HEX : d.color, borderRadius: [0, 4, 4, 0] },
                                })),
                                barWidth: '50%',
                                label: { show: true, position: 'right', fontSize: 14, fontWeight: 'bold', color: '#000', formatter: (p) => formatNumber(p.value, 2) },
                                emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.12)' } },
                            }],
                            grid: { top: 60, right: 50, bottom: 55, left: 24, containLabel: true },
                        }}
                        theme="geomotica"
                        style={{ height: 340, width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                        notMerge
                        lazyUpdate
                    />
                </ChartContainer>
            </ChartsRow>
        </div>
    );

    /* ───────────────── Gráficas por Vuelo (ECharts lines) ───────────────── */
    const buildLineOption = (dataArray, yLabel, yMin, yMax) => ({
        tooltip: { trigger: 'axis', ...glassTooltip },
        xAxis: {
            type: 'category',
            data: kmlIndices.map(String),
            axisLabel: { fontSize: 12, fontWeight: 'bold', interval: Math.max(0, Math.ceil(maxLen / 10) - 1) },
            name: 'VUELOS / KML',
            nameLocation: 'middle',
            nameGap: 44,
            nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
        },
        yAxis: {
            type: 'value',
            min: yMin,
            max: yMax,
            axisLabel: { fontSize: 12, fontWeight: 'bold' },
            name: yLabel,
            nameLocation: 'middle',
            nameGap: 52,
            nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
            splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
        },
        series: [{
            type: 'line',
            data: kmlIndices.map((_, idx) => dataArray[idx] !== undefined ? dataArray[idx] : null),
            smooth: false,
            symbol: 'circle',
            symbolSize: 6,
            connectNulls: false,
            lineStyle: { width: 2.5, color: PRIMARY_COLOR_HEX },
            itemStyle: { color: PRIMARY_COLOR_HEX },
            areaStyle: {
                color: {
                    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: [
                        { offset: 0, color: PRIMARY_COLOR_HEX + '26' },
                        { offset: 1, color: 'transparent' },
                    ],
                },
            },
        }],
        grid: { top: 50, right: 60, bottom: 64, left: 64, containLabel: true },
        dataZoom: maxLen > 30 ? [{ type: 'inside' }, { type: 'slider', height: 20 }] : [],
    });

    const lineLitros = (
        <ChartContainer className="apsLineChart">
            <h4 style={{ textAlign: "center", marginBottom: 10, fontSize: "1.5em", fontWeight: "bold" }}>
                {amountChartTitle}
            </h4>
            {dosisRealesArray.length === 0 ? (
                <EmptyState>No hay datos</EmptyState>
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={buildLineOption(dosisRealesArray, amountUnitLabel, yMinLitros, yMaxLitros)}
                    theme="geomotica"
                    style={{ height: 340, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </ChartContainer>
    );

    const lineDosisAreas = (
        <ChartContainer className="apsLineChart">
            <h4 style={{ textAlign: "center", marginBottom: 10, fontSize: "1.5em", fontWeight: "bold" }}>
                {rateChartTitle}
            </h4>
            {dosisAreasArray.length === 0 ? (
                <EmptyState>No hay datos</EmptyState>
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={buildLineOption(dosisAreasArray, rateUnitLabel, yMinDosisAreas, yMaxDosisAreas)}
                    theme="geomotica"
                    style={{ height: 340, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </ChartContainer>
    );

    const lineAlturas = (
        <ChartContainer className="apsLineChart">
            <h4 style={{ textAlign: "center", marginBottom: 10, fontSize: "1.5em", fontWeight: "bold" }}>
                ALTURA POR VUELO
            </h4>
            {alturasNoZero.length === 0 ? (
                <EmptyState>No hay datos</EmptyState>
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={buildLineOption(alturasForLine, `Altura media (${alturaUnitLabel})`, yMinAlturas, yMaxAlturas)}
                    theme="geomotica"
                    style={{ height: 340, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </ChartContainer>
    );

    /* ───────────────── Histogramas (ECharts) ───────────────── */
    const histAlturaData = buildHistogram(alturasNoZero, 3);
    const histLitrosData = buildHistogram(dosisRealesArray, 6);
    const histDosisAreasData = buildHistogram(dosisAreasArray, 6);

    const buildHistOption = (data, yLabel) => ({
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const p = params[0];
                return `<strong>${p.name}</strong><br/>${p.value} VUELOS/KML`;
            },
            ...glassTooltip,
        },
        xAxis: {
            type: 'value',
            axisLabel: { fontSize: 12, fontWeight: 'bold', formatter: (v) => Math.round(v).toString() },
            name: 'VUELOS/KML',
            nameLocation: 'middle',
            nameGap: 52,
            nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
            splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
        },
        yAxis: {
            type: 'category',
            data: data.map(d => d.rango),
            axisLabel: { fontSize: 12, fontWeight: 'bold' },
            name: yLabel,
            nameLocation: 'middle',
            nameGap: 112,
            nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
        },
        series: [{
            type: 'bar',
            data: data.map(d => ({
                value: d.vuelos,
                itemStyle: { color: d.color === PRIMARY_COLOR ? PRIMARY_COLOR_HEX : (d.color === PRIMARY_COLOR_DARK ? PRIMARY_COLOR_DARK_HEX : d.color), borderRadius: [0, 4, 4, 0] },
            })),
            barWidth: '60%',
            label: { show: true, position: 'right', fontSize: 14, fontWeight: 'bold', color: '#000' },
            emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.12)' } },
        }],
        grid: { top: 50, right: 64, bottom: 72, left: 148, containLabel: true },
    });

    const histAltura = (
        <ChartContainer className="apsHistChart">
            <h4 style={{ textAlign: "center", marginBottom: 10, fontSize: "1.5em", fontWeight: "bold" }}>
                Histograma de altura ({alturaUnitLabel})
            </h4>
            {alturasNoZero.length === 0 ? (
                <EmptyState>No hay datos</EmptyState>
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={buildHistOption(histAlturaData, `Altura de vuelos (${alturaUnitLabel})`)}
                    theme="geomotica"
                    style={{ height: 340, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </ChartContainer>
    );

    const histLitros = (
        <ChartContainer className="apsHistChart">
            <h4 style={{ textAlign: "center", marginBottom: 10, fontSize: "1.5em", fontWeight: "bold" }}>
                {isSolidApplication ? 'Histograma de kilos aplicados' : 'Histograma de litros aplicados'}
            </h4>
            {dosisRealesArray.length === 0 ? (
                <EmptyState>No hay datos</EmptyState>
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={buildHistOption(histLitrosData, amountUnitLabel)}
                    theme="geomotica"
                    style={{ height: 340, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </ChartContainer>
    );

    const histDosisAreas = (
        <ChartContainer className="apsHistChart">
            <h4 style={{ textAlign: "center", marginBottom: 10, fontSize: "1.5em", fontWeight: "bold" }}>
                Histograma de dosis áreas (L/ha)
            </h4>
            {dosisAreasArray.length === 0 ? (
                <EmptyState>No hay datos</EmptyState>
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={buildHistOption(histDosisAreasData, rateUnitLabel)}
                    theme="geomotica"
                    style={{ height: 340, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </ChartContainer>
    );

    /* ───────────────── Render final ───────────────── */
    return (
        <div ref={apsChartsRef} style={{ display: "contents" }}>
            {/* Indicadores de Área */}
            {renderCategory(
                "Indicadores de Área",
                isTasaVariable ? varAreaIndicators : charts,
                true
            )}

            {/* Gráficas por Vuelo */}
            {renderCategory(
                "Gráficas por Vuelo",
                <ChartsRow>
                    {lineAlturas}
                    {lineLitros}
                    {lineDosisAreas}
                </ChartsRow>,
                true
            )}
        </div>
    );
};
